import { noop } from '@mail-core/std';
import { Spacing } from '@vkontakte/vkui';
import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { WhatsNewDialog } from 'reactApp/components/WhatsNewDialog/WhatsNewDialog';
import {
    type UnlimitedDocumentsEntryPoint,
    type UnlimitedDocumentsTariffType,
    getUnlimitedDocumentsTypeNameGenitive,
    UNLIMITED_DOCUMENTS_RADAR_NAMES,
} from 'reactApp/modules/unlimitedDocuments/unlimitedDocuments.const';
import { UnlimitedDocumentsNote } from 'reactApp/ui/UnlimitedDocuments/UnlimitedDocumentsNote/UnlimitedDocumentsNote';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';
import { isDarkThemeModeEnabled } from 'reactApp/utils/theme';

import styles from './UnlimitedDocumentsModal.css';
import { getUnlimitedDocumentsModalCoverImageUrl } from './UnlimitedDocumentsModal.helpers';

export interface UnlimitedDocumentsModalProps {
    onShow?: () => void;
    onClick?: () => void;
    onCancel?: () => void;
    onClose?: () => void;

    type: UnlimitedDocumentsTariffType;
    offer_source: UnlimitedDocumentsEntryPoint;
}

export const UnlimitedDocumentsModal = memo<UnlimitedDocumentsModalProps>(
    ({ onShow = noop, onClick = noop, onClose = noop, onCancel = noop, type, offer_source }) => {
        const isDarkTheme = isDarkThemeModeEnabled();

        const coverImage = getUnlimitedDocumentsModalCoverImageUrl(isDarkTheme, type);

        const { header, text } = useMemo(() => {
            const typeNameGenitive = getUnlimitedDocumentsTypeNameGenitive(type);

            return {
                header: (
                    <>
                        Получите дополнительное
                        <br />
                        место для {typeNameGenitive}
                    </>
                ),
                text: (
                    <>
                        Подключите любую подписку Mail Space,
                        <br />
                        чтобы не удалять старые файлы ради новых {typeNameGenitive}
                    </>
                ),
            };
        }, [type]);

        const analyticParams = useMemo(() => {
            return {
                doc_type: type,
                offer_source,
            };
        }, [offer_source, type]);

        const sendAnalytics = useCallback(
            (action: string) => {
                sendPaymentGa({
                    action,
                    name: UNLIMITED_DOCUMENTS_RADAR_NAMES.FAKEDOOR_MODAL,
                    eventCategory: ECategoryGa.modal,
                    ...analyticParams,
                });
            },
            [analyticParams]
        );

        useEffect(() => {
            sendAnalytics('show');
            emitAnalyticEvent(AnalyticEventNames.UNLIMITED_DOCUMENTS_MODAL_SHOW, analyticParams);
            onShow();
        }, [sendAnalytics, analyticParams, onShow]);

        const handleOnPrimaryClick = useCallback(() => {
            sendAnalytics('click');
            emitAnalyticEvent(AnalyticEventNames.UNLIMITED_DOCUMENTS_MODAL_CLICK, analyticParams);
            onClick();
        }, [sendAnalytics, analyticParams, onClick]);

        const handleOnCancel = useCallback(() => {
            sendAnalytics('close');
            emitAnalyticEvent(AnalyticEventNames.UNLIMITED_DOCUMENTS_MODAL_CLOSE, analyticParams);
            onCancel();
        }, [sendAnalytics, analyticParams, onCancel]);

        return (
            <WhatsNewDialog
                imageUrl={coverImage}
                imageClassName={styles.image}
                footerClassName={styles.footer}
                title={header}
                content={
                    <>
                        <Spacing size={12} />
                        <div className={styles.text}>{text}</div>
                        <Spacing size={24} />
                    </>
                }
                primaryButtonText={'Подключить за 0 ₽'}
                onClick={handleOnPrimaryClick}
                onClose={onClose}
                onDismiss={handleOnCancel}
                imageBgColor="var(--vkui--color_background_modal)"
                buttonTheme="vk"
                dialogSize="tiny"
                qaId="unlimited-documents-modal"
                footerText={
                    <div className={styles.footerNote}>
                        <UnlimitedDocumentsNote />
                    </div>
                }
                footerTextRightToButtons
                isRebranding
            />
        );
    }
);

UnlimitedDocumentsModal.displayName = 'UnlimitedDocumentsModal';
